import type * as React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import type { ImportColumnMapping, ImportColumnTarget } from "@/types/data-import.types";
import {
  LOSS_TYPE_LABELS,
  PRODUCTION_ROLE_LABELS,
  type LossTypeValue,
} from "@/types/parameters.types";

export interface LineOption {
  id: string;
  name: string;
}
export interface ProductOption {
  id: string;
  name: string;
}

interface ColumnTargetRowProps {
  column: string;
  sample: string;
  decision: ImportColumnMapping | undefined;
  lines: LineOption[];
  products: ProductOption[];
  onChange: (decision: ImportColumnMapping) => void;
  /**
   * Spec 084 — o que desenhar quando a coluna descreve a BATELADA. Vem pronto de fora porque
   * depende de duas leituras (características declaradas e grafias contadas) que são do passo, e
   * não desta linha.
   */
  characteristicSlot?: React.ReactNode;
  /** Sem característica declarada, a decisão nem é oferecida: escolher o que não existe trava. */
  characteristicAvailable?: boolean;
}

// O vocabulário é o CANÔNICO de `types/parameters.types.ts`, não uma cópia local. A cópia que
// existia aqui já tinha divergido — chamava REJECT de "Rejeito" e WASTE de "Resíduo" — enquanto o
// resto da plataforma (ScrapEntryBlock, LossesCard, narração do assistente) diz "Refugo" e
// "Desperdício": o gestor mapeava uma coluna com um nome e via a perda importada com outro.
const ROLE_LABEL: Record<ImportColumnTarget["role"], string> = PRODUCTION_ROLE_LABELS;
const LOSS_LABEL: Record<LossTypeValue, string> = LOSS_TYPE_LABELS;

// O destino em branco começa inválido de propósito: o contrato exige uuid em linha e produto, e é
// a escolha do gestor que o completa. Deixá-lo pré-preenchido com a primeira linha da lista faria
// um mapeamento silenciosamente errado passar por completo.
const EMPTY_TARGET = { lineId: "", productId: "", role: "PRODUCES" } as ImportColumnTarget;

/** A decisão que a escolha do seletor representa. `CHARACTERISTIC` nasce sem característica. */
function decisionFor(value: string): ImportColumnMapping {
  if (value === "IGNORE") return { action: "IGNORE" };
  if (value === "CHARACTERISTIC") {
    return { action: "CHARACTERISTIC", characteristicId: "", translations: {} };
  }
  return { action: "MAP", targets: [EMPTY_TARGET] };
}

/**
 * Uma coluna do arquivo e o que ela vira. A coluna pode ter MAIS DE UM destino: é assim que a
 * mesma coluna é saída de uma linha e consumo da seguinte, que é o caso real da fábrica.
 */
export function ColumnTargetRow({
  column,
  sample,
  decision,
  lines,
  products,
  onChange,
  characteristicSlot,
  characteristicAvailable = false,
}: ColumnTargetRowProps) {
  const targets = decision?.action === "MAP" ? decision.targets : [];
  const isIgnored = decision?.action === "IGNORE";
  const isCharacteristic = decision?.action === "CHARACTERISTIC";

  function setTarget(index: number, patch: Partial<ImportColumnTarget>) {
    const next = targets.map((t, i) => (i === index ? { ...t, ...patch } : t));
    // Trocar o sentido para fora de Refuga tira o tipo de perda junto: mantê-lo faria o contrato
    // recusar um formulário que parece completo na tela.
    const cleaned = next.map((t) =>
      t.role === "SCRAPS"
        ? { ...t, lossType: t.lossType ?? "REJECT" }
        : { ...t, lossType: undefined }
    ) as ImportColumnTarget[];
    onChange({ action: "MAP", targets: cleaned });
  }

  return (
    <div className="space-y-2 border-b border-border-default p-3 last:border-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-[14px] font-medium text-text-primary">{column}</div>
          <div className="truncate font-mono text-[12px] text-text-tertiary">
            {sample ? `Exemplo: ${sample}` : "Sem exemplo nesta amostra"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select
            aria-label={`O que fazer com a coluna ${column}`}
            value={
              isIgnored
                ? "IGNORE"
                : isCharacteristic
                  ? "CHARACTERISTIC"
                  : targets.length > 0
                    ? "MAP"
                    : ""
            }
            onChange={(e) => onChange(decisionFor(e.target.value))}
          >
            <option value="">Sem decisão</option>
            <option value="MAP">Registrar na produção</option>
            {characteristicAvailable ? (
              <option value="CHARACTERISTIC">Marcar a batelada</option>
            ) : null}
            <option value="IGNORE">Ignorar esta coluna</option>
          </Select>
        </div>
      </div>

      {isCharacteristic ? characteristicSlot : null}

      {targets.map((t, i) => (
        <div key={i} className="grid gap-2 md:grid-cols-[1fr_1fr_auto_auto_auto]">
          <Select
            aria-label="Linha de produção"
            value={t.lineId}
            onChange={(e) => setTarget(i, { lineId: e.target.value })}
          >
            <option value="">Linha de produção</option>
            {lines.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Produto"
            value={t.productId}
            onChange={(e) => setTarget(i, { productId: e.target.value })}
          >
            <option value="">Produto</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Sentido"
            value={t.role}
            onChange={(e) => setTarget(i, { role: e.target.value as ImportColumnTarget["role"] })}
          >
            {(Object.keys(ROLE_LABEL) as ImportColumnTarget["role"][]).map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </Select>
          {t.role === "SCRAPS" ? (
            <Select
              aria-label="Tipo de perda"
              value={t.lossType ?? "REJECT"}
              onChange={(e) =>
                setTarget(i, { lossType: e.target.value as ImportColumnTarget["lossType"] })
              }
            >
              {(Object.keys(LOSS_LABEL) as (keyof typeof LOSS_LABEL)[]).map((l) => (
                <option key={l} value={l}>
                  {LOSS_LABEL[l]}
                </option>
              ))}
            </Select>
          ) : (
            <div aria-hidden />
          )}
          <Button
            variant="outline"
            size="sm"
            aria-label={`Remover destino ${i + 1} da coluna ${column}`}
            onClick={() =>
              onChange({
                action: "MAP",
                targets: targets.filter((_, j) => j !== i),
              })
            }
            disabled={targets.length === 1}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      ))}

      {decision?.action === "MAP" ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onChange({ action: "MAP", targets: [...targets, EMPTY_TARGET] })}
        >
          <Plus size={14} className="mr-1" />
          Outro destino para esta coluna
        </Button>
      ) : null}
    </div>
  );
}
